<template>
    <div class="datetime-selector">
        <v-btn-toggle
            v-model="__type"
            :disabled="disabled"
            rounded="md"
            color="primary"
            variant="flat"
            group
            mandatory
            class="datetime-selector__btns"
        >
            <v-btn v-for="dt in _types" :key="dt" :value="dt" class="datetime-selector__btn">
                {{ t(`common.resources.dateTimePicker.${camelCase(DateType[dt])}`, DateType[dt]) }}
            </v-btn>
        </v-btn-toggle>
        <div v-if="showDateTimePicker && _modelValue" class="mt-2 w-100">
            <date-time-picker
                v-model="_modelValue"
                :min-date="minDate"
                :max-date="maxDate"
                :disabled="disabled"
                density="compact"
                variant="outlined"
                color="primary"
                base-color="grey"
                bg-color="white"
                active-class="elevation-4"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { DateType } from '@/features/common/models';
import { DateTimePicker } from '@geta/kolumbus-frontend/components';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { startOfMinute } from 'date-fns';
import { camelCase } from 'lodash-es';
import { toLocalDate } from '@geta/kolumbus-frontend/composables';

interface Props {
    /**
     * The model value for the date time picker.
     */
    modelValue?: Date;

    /**
     * The type of the date time picker.
     * @default DateType.Now
     */
    type?: DateType;

    /**
     * The types of the date time picker.
     * @default [DateType.Now, DateType.DepartAt]
     */
    types?: DateType[];

    /**
     * The minimum date of the date time picker.
     */
    minDate?: Date | number;

    /**
     * The maximum date of the date time picker.
     */
    maxDate?: Date | number;

    /**
     * Whether the date time picker is disabled.
     */
    disabled?: boolean;

    /**
     * The time zone to use for the date time picker.
     * @default 'Europe/Oslo'
     */
    timeZone?: string;
}

const { t } = useI18n();
const props = withDefaults(defineProps<Props>(), { type: DateType.Now });
const _modelValue = defineModel<Date | undefined>('modelValue');
const _type = defineModel<DateType>('type', { default: DateType.Now });
const localType = ref<DateType>(_type.value);
const __type = computed({
    get() {
        return _type.value || localType.value;
    },
    set(value) {
        _type.value = localType.value = value;

        if (value === DateType.Now) {
            _modelValue.value = undefined;
        } else if (!_modelValue.value) {
            _modelValue.value = startOfMinute(toLocalDate(new Date(), props.timeZone));
        }
    }
});
const _types = computed(() => props.types || [DateType.Now, DateType.DepartAt]);
const showDateTimePicker = computed(() => __type.value !== DateType.Now);
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.datetime-selector {
    &__btns {
        width: 100%;
        display: flex;
        flex-direction: row;

        &.v-btn-group--density-default.v-btn-group {
            height: 40px;
        }

        &.v-btn-group .v-btn:not(:last-child) {
            border-inline-end: 1px solid var(--v-color-grey-lighten-2);
        }

        &.v-btn-group .v-btn--active,
        &.v-btn-group .v-btn:has(+ .v-btn--active) {
            border-inline-end: none !important;
        }
    }

    &__btn {
        flex: 1 1 0;
        border: 1px solid var(--v-color-grey-lighten-2);
        font-size: calc(18px / $font-size-root) * 1rem;
        font-weight: 400;

        &.v-btn--active {
            font-weight: 700;
            border: 0 none;
        }
    }

    .datetime-picker .v-input {
        font-weight: 400;
    }
}
</style>
